import React from "react";
import { useBlocker } from "react-router-dom";
import { GenericError } from "../Feedback/GenericError";
import { useTabsWithHashBlocker } from "../Layout/TabsWithHash";
import { DefaultUnsavedChangesConfirmationDialogProps } from "./constants";
import { BlockerContext, useNavigationBlockerContext } from "./context";

export {
  NavigationBlockerProvider,
  useNavigationBlockerContext,
} from "./context";

export type NavigationBlockerProps = { hasChanges: boolean };

/**
 * NavigationBlocker prevents users from accidentally navigating away from a page with unsaved changes.
 *
 * ## Features:
 * - Blocks React Router navigation between different routes when `hasChanges` is true
 * - Shows a confirmation dialog when blocked navigation is attempted
 * - Prevents browser refresh/close with native browser confirmation dialog
 * - Supports hash-based tab navigation blocking
 * - Can be temporarily disabled via NavigationBlockerProvider context
 *
 * ## Usage:
 * Must be wrapped in a NavigationBlockerProvider to enable disable functionality:
 *
 * ```tsx
 * <NavigationBlockerProvider>
 *   <NavigationBlocker hasChanges={hasUnsavedData} />
 * </NavigationBlockerProvider>
 * ```
 *
 * ## Temporarily Disabling:
 * From any component within the provider, you can disable blocking:
 *
 * ```tsx
 * const { disable } = useNavigationBlockerContext();
 *
 * const handleSaveAndNavigate = async () => {
 *   await disable(true);  // Temporarily disable
 *   navigate('/next-page');
 *   await disable(false); // Re-enable blocking
 * };
 * ```
 *
 * ## Using Confirmation Dialog Elsewhere:
 * You can also use the confirmation dialog for other interactions:
 *
 * ```tsx
 * const showConfirmation = useConfirmationDialog();
 *
 * const handleDelete = () => {
 *   showConfirmation({
 *     onConfirm: () => deleteItem(),
 *     onCancel: () => console.log('Cancelled'),
 *     dialogProps: {
 *       header: "Delete item?",
 *       body: "This cannot be undone.",
 *     }
 *   });
 * };
 * ```
 *
 * @param hasChanges - When true, blocks navigation and shows confirmation dialogs
 */
export function NavigationBlocker({ hasChanges }: NavigationBlockerProps) {
  const context = React.useContext(BlockerContext);
  const blockerId = React.useId();
  const isDisabled = context?.isDisabled ?? false;
  const showConfirmation = context?.confirm;
  const reportHasChanges = context?.reportHasChanges;

  React.useEffect(() => {
    if (!reportHasChanges) return;
    reportHasChanges(blockerId, hasChanges);
    return () => reportHasChanges(blockerId, false);
  }, [blockerId, hasChanges, reportHasChanges]);

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    return (
      !isDisabled &&
      !!hasChanges &&
      (context?.includeQueryParams === true
        ? currentLocation.search !== nextLocation.search ||
          currentLocation.pathname !== nextLocation.pathname
        : currentLocation.pathname !== nextLocation.pathname)
    );
  });

  useTabsWithHashBlocker(hasChanges, {
    ...DefaultUnsavedChangesConfirmationDialogProps,
    ...context?.confirmationConfig?.dialogProps,
  });

  // Show/hide confirmation dialog based on blocker state
  React.useEffect(() => {
    if (blocker.state === "blocked" && showConfirmation) {
      showConfirmation().then((confirmed) => {
        if (confirmed) {
          blocker.proceed?.();
        } else {
          blocker.reset?.();
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocker]);

  // Add beforeunload event listener to prevent page refresh/close using browser's native confirmation dialog
  React.useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasChanges) {
        e.preventDefault();
      }
    };

    if (hasChanges && !isDisabled) {
      window.addEventListener("beforeunload", handleBeforeUnload);
    }

    // Cleanup the event listener
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isDisabled, hasChanges]);

  if (!context) {
    return (
      <GenericError
        message="NavigationBlocker must be used within a NavigationBlockerProvider"
        variant="inline"
      />
    );
  }

  return null;
}

/**
 * Hook to access the confirmation dialog from any component within NavigationBlockerProvider.
 * Returns a function that shows a confirmation dialog and resolves to a boolean indicating
 * whether the user confirmed or cancelled.
 *
 * @returns A function that shows the confirmation dialog and returns a Promise<boolean>
 *
 * @example
 * ```tsx
 * const confirm = useNavigationBlocker();
 *
 * const handleDelete = async () => {
 *   if (await confirm()) {
 *     // perform delete
 *   }
 * };
 * ```
 */
export const useNavigationBlocker = () => {
  const context = useNavigationBlockerContext();
  return context.confirm;
};

/**
 * Component that provides conditional access to the confirmation dialog based on whether
 * there are unsaved changes. When hasChanges is false, provides a function that always
 * resolves to true (skipping confirmation). When hasChanges is true, provides the actual
 * confirmation dialog function.
 *
 * @param hasChanges - Whether there are unsaved changes that require confirmation
 * @param children - Render prop that receives the confirm function
 *
 * @example
 * ```tsx
 * <WithNavigationBlocker hasChanges={isDirty}>
 *   {(confirm) => (
 *     <button onClick={async () => {
 *       if (await confirm()) navigate('/next-page');
 *     }}>
 *       Navigate Away
 *     </button>
 *   )}
 * </WithNavigationBlocker>
 * ```
 */
type WithNavigationBlockerProps = {
  hasChanges: boolean;
  children: (confirm: () => Promise<boolean>) => React.ReactNode;
};
export const WithNavigationBlocker: React.FC<WithNavigationBlockerProps> = ({
  hasChanges,
  children,
}) => {
  const confirm = useNavigationBlocker();
  const skipConfirm = React.useCallback(async (): Promise<boolean> => {
    return true;
  }, []);

  return (
    <>
      <NavigationBlocker hasChanges={hasChanges} />
      {hasChanges ? children(confirm) : children(skipConfirm)}
    </>
  );
};
